<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ExternalVideoSlideOver from '@/components/organizer/galleries/ExternalVideoSlideOver.vue'
import GalleryDropZone from '@/components/organizer/galleries/GalleryDropZone.vue'
import GalleryEmptyState from '@/components/organizer/galleries/GalleryEmptyState.vue'
import GalleryFormSlideOver from '@/components/organizer/galleries/GalleryFormSlideOver.vue'
import GalleryItemSlideOver from '@/components/organizer/galleries/GalleryItemSlideOver.vue'
import GalleryMediaGrid from '@/components/organizer/galleries/GalleryMediaGrid.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import GalleryPreflightBanner from '@/components/organizer/galleries/GalleryPreflightBanner.vue'
import GallerySelectionBar from '@/components/organizer/galleries/GallerySelectionBar.vue'
import GalleryStorageBanner from '@/components/organizer/galleries/GalleryStorageBanner.vue'
import GalleryStorageMeter from '@/components/organizer/galleries/GalleryStorageMeter.vue'
import MoveItemsSlideOver from '@/components/organizer/galleries/MoveItemsSlideOver.vue'
import { useEvents } from '@/composables/useEvents'
import { useGalleries } from '@/composables/useGalleries'
import { useGallery } from '@/composables/useGallery'
import { useGallerySelection } from '@/composables/useGallerySelection'
import { useGalleryStorage } from '@/composables/useGalleryStorage'
import { useGalleryToast } from '@/composables/useGalleryToast'
import { UploadStatus, useGalleryUploadManager } from '@/composables/useGalleryUploadManager'
import { CoverMediaStatus, GalleryItemStatus, GalleryItemType, GalleryStatus, GalleryType } from '@/types/galleries'
import type {
  ExternalVideoPayload,
  GalleryStorageLimits,
  OrganizerGallery,
  OrganizerGalleryItem,
} from '@/types/galleries'
import type { GalleryGridFilter, TileAction, UploadTileAction } from '@/types/galleryUi'
import { eventSiteUrl } from '@/utils/eventSiteUrl'
import { formatBytes } from '@/utils/formatBytes'
import { parseExternalVideoUrl } from '@/utils/galleries/externalVideoUrl'

const KEEP_ORIGINAL_KEY = 'galleries.keep_original_quality'

// Used until the storage summary has been loaded; the server enforces the real limits.
const FALLBACK_LIMITS: GalleryStorageLimits = {
  image_max_bytes: 15 * 1024 * 1024,
  max_files_per_batch: 50,
  max_items_per_gallery: 500,
  allowed_image_mimes: ['image/jpeg', 'image/png', 'image/webp'],
  allowed_video_mimes: ['video/mp4', 'video/webm'],
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useGalleryToast()
const manager = useGalleryUploadManager()

const eventSlug = route.params['eventSlug'] as string
const galleryId = route.params['galleryId'] as string

const { event, fetchEvent } = useEvents()
const detail = useGallery(eventSlug, galleryId)
const { gallery, items, loading, error } = detail
const storage = useGalleryStorage(eventSlug)
const galleryList = useGalleries(eventSlug)

const filter = ref<GalleryGridFilter>('all')
const keepOriginal = ref(localStorage.getItem(KEEP_ORIGINAL_KEY) === 'true')
const dragOverlay = ref(false)
let dragDepth = 0

const selection = useGallerySelection(() => visibleSelectable.value)

const itemPanelId = ref<string | null>(null)
const itemPanelOpen = ref(false)
const linkPanelOpen = ref(false)
const formPanelMode = ref<'edit' | 'create' | null>(null)
const movePanelOpen = ref(false)
const moveIds = ref<string[]>([])
const moveAfterCreate = ref(false)
const deleteIds = ref<string[] | null>(null)
const deletingItems = ref(false)
const deleteGalleryOpen = ref(false)
const deletingGallery = ref(false)

const ownEntries = computed(() =>
  manager.entries.value.filter((e) => e.eventSlug === eventSlug && e.galleryId === galleryId),
)
const notices = computed(() =>
  manager.notices.value.filter((n) => n.eventSlug === eventSlug && n.galleryId === galleryId),
)

const limits = computed(() => storage.summary.value?.limits ?? FALLBACK_LIMITS)
const uploadsBlocked = computed(() => storage.summary.value?.uploads_blocked === true)
const isPublished = computed(() => gallery.value?.status === GalleryStatus.Published)
const isExternal = computed(() => gallery.value?.type === GalleryType.External)
const coverProcessing = computed(
  () =>
    gallery.value?.cover_media_status === CoverMediaStatus.Pending ||
    gallery.value?.cover_media_status === CoverMediaStatus.Processing,
)
const coverFailed = computed(() => gallery.value?.cover_media_status === CoverMediaStatus.Failed)
const hasContent = computed(() => items.value.length > 0 || ownEntries.value.some((e) => e.itemId === null))

const visibleSelectable = computed(() => {
  const list =
    filter.value === 'missing_alt' ? items.value.filter((i) => isMissingAlt(i.type, i.status, i.alt_text)) : items.value
  return list.map((i) => ({ id: i.id, bytes: i.bytes }))
})

const missingAltCount = computed(
  () => items.value.filter((i) => isMissingAlt(i.type, i.status, i.alt_text)).length,
)

function isMissingAlt(type: string, status: string, altText: string | null): boolean {
  return type === GalleryItemType.Image && status === GalleryItemStatus.Ready && (altText ?? '').trim() === ''
}

const summaryLine = computed(() => {
  const g = gallery.value
  if (g === null) return ''
  const parts: string[] = []
  if (g.counts.images > 0) parts.push(t('galleries.card.photos', g.counts.images))
  const videos = g.counts.videos + g.counts.external_videos
  if (videos > 0) parts.push(t('galleries.card.videos', videos))
  if (parts.length === 0) parts.push(t('galleries.card.empty'))
  parts.push(formatBytes(g.bytes_used))
  return parts.join(' - ')
})

// An external gallery has no deep link of its own: point to the galleries section instead.
const previewUrl = computed(() =>
  eventSiteUrl(eventSlug, isExternal.value ? 'galleries' : `gallery-${galleryId}`),
)

const deleteItemsBody = computed(() => {
  const ids = deleteIds.value ?? []
  const bytes = items.value.filter((i) => ids.includes(i.id)).reduce((sum, i) => sum + i.bytes, 0)
  return t('galleries.confirm.delete_items_body', { size: formatBytes(bytes) })
})

// ── Loading ──────────────────────────────────────────────────────────────────

onMounted(() => {
  void fetchEvent(eventSlug)
  void detail.refresh()
  void storage.fetch()
  void galleryList.fetch()
  window.addEventListener('dragenter', onWindowDragEnter)
  window.addEventListener('dragover', onWindowDragOver)
  window.addEventListener('dragleave', onWindowDragLeave)
  window.addEventListener('drop', onWindowDrop)
  document.addEventListener('keydown', onDocumentKeydown)
  document.addEventListener('paste', onDocumentPaste)
})

const offCreated = manager.onItemCreated((entry) => {
  if (entry.eventSlug === eventSlug) void storage.fetch()
})

onBeforeUnmount(() => {
  offCreated()
  window.removeEventListener('dragenter', onWindowDragEnter)
  window.removeEventListener('dragover', onWindowDragOver)
  window.removeEventListener('dragleave', onWindowDragLeave)
  window.removeEventListener('drop', onWindowDrop)
  document.removeEventListener('keydown', onDocumentKeydown)
  document.removeEventListener('paste', onDocumentPaste)
})

watch(error, (value) => {
  if (value === 'not_found') {
    toast.push(t('galleries.errors.not_found'), { kind: 'error' })
    void router.replace(`/organizer/events/${eventSlug}/galleries`)
  }
})

watch(keepOriginal, (value) => localStorage.setItem(KEEP_ORIGINAL_KEY, String(value)))

// ── Uploads ──────────────────────────────────────────────────────────────────

function handleFiles(files: File[]): void {
  if (files.length === 0) return
  if (uploadsBlocked.value) {
    toast.push(t('galleries.dropzone.blocked_toast'), { kind: 'error' })
    return
  }
  manager.enqueue(eventSlug, galleryId, files, {
    keepOriginal: keepOriginal.value,
    limits: limits.value,
    remainingBytes: storage.summary.value?.remaining_bytes ?? Number.MAX_SAFE_INTEGER,
  })
}

function hasFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

function onWindowDragEnter(event: DragEvent): void {
  if (isExternal.value || !hasFiles(event)) return
  dragDepth += 1
  dragOverlay.value = true
}

function onWindowDragOver(event: DragEvent): void {
  if (!isExternal.value && hasFiles(event)) event.preventDefault()
}

function onWindowDragLeave(event: DragEvent): void {
  if (!hasFiles(event)) return
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) dragOverlay.value = false
}

function onWindowDrop(event: DragEvent): void {
  dragDepth = 0
  dragOverlay.value = false
  if (isExternal.value || !hasFiles(event)) return
  event.preventDefault()
  // The drop zone handles its own drop and marks the event as handled.
  if (event.defaultPrevented && event.target instanceof Element && event.target.closest('[data-testid="gallery-drop-zone"]')) return
  handleFiles(Array.from(event.dataTransfer?.files ?? []))
}

function onUploadAction(action: UploadTileAction): void {
  if (action.type === 'cancel') manager.cancel(action.id)
  else if (action.type === 'retry') manager.retry(action.id)
  else manager.remove(action.id)
}

// ── Keyboard and paste shortcuts ─────────────────────────────────────────────

function anyPanelOpen(): boolean {
  return (
    itemPanelOpen.value ||
    linkPanelOpen.value ||
    formPanelMode.value !== null ||
    movePanelOpen.value ||
    deleteIds.value !== null ||
    deleteGalleryOpen.value
  )
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (isExternal.value || anyPanelOpen()) return
  if (event.key === 'Escape' && selection.count.value > 0) {
    selection.clear()
    return
  }
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a' && !isEditable(event.target)) {
    const grid = document.querySelector('[data-testid="gallery-media-grid"]')
    if (grid !== null && event.target instanceof Node && grid.contains(event.target)) {
      event.preventDefault()
      selection.selectAll()
    }
  }
}

function onDocumentPaste(event: ClipboardEvent): void {
  if (isExternal.value || anyPanelOpen() || isEditable(event.target)) return
  const text = event.clipboardData?.getData('text') ?? ''
  const parsed = parseExternalVideoUrl(text)
  if (parsed === null) return
  event.preventDefault()
  void addLinkWithUndo({ url: parsed.normalizedUrl, credit: gallery.value?.default_credit ?? null })
}

async function addLinkWithUndo(payload: ExternalVideoPayload): Promise<void> {
  const result = await detail.addExternalVideo(payload)
  if (result.item === null) {
    toast.push(
      result.code === 'duplicate_external_video' ? t('galleries.link.duplicate') : t('galleries.errors.generic'),
      { kind: 'error' },
    )
    return
  }
  const createdId = result.item.id
  toast.push(t('galleries.link.added_toast'), {
    kind: 'success',
    actionLabel: t('galleries.link.undo'),
    onAction: () => {
      void detail.deleteItem(createdId)
    },
  })
}

// ── Item actions ─────────────────────────────────────────────────────────────

function itemById(id: string): OrganizerGalleryItem | undefined {
  return items.value.find((i) => i.id === id)
}

async function hideItems(ids: string[], hidden: boolean): Promise<void> {
  const ok = await detail.setHidden(ids, hidden)
  if (!ok) return
  toast.push(hidden ? t('galleries.toast.hidden', ids.length) : t('galleries.toast.shown', ids.length), {
    kind: 'success',
    actionLabel: t('galleries.link.undo'),
    onAction: () => {
      void detail.setHidden(ids, !hidden)
    },
  })
}

function openMove(ids: string[]): void {
  moveIds.value = ids
  moveAfterCreate.value = false
  movePanelOpen.value = true
}

async function handleTileAction(action: TileAction): Promise<void> {
  const item = itemById(action.id)
  if (item === undefined) return
  switch (action.type) {
    case 'hide-toggle':
      await hideItems([item.id], !item.is_hidden)
      break
    case 'set-cover':
      if (await detail.setCover(item.id)) toast.push(t('galleries.toast.cover_set'), { kind: 'success' })
      break
    case 'move-to':
      openMove([item.id])
      break
    case 'move-to-start':
      detail.moveItem(item.id, 'start')
      break
    case 'move-to-end':
      detail.moveItem(item.id, 'end')
      break
    case 'delete':
      deleteIds.value = [item.id]
      break
    case 'retry': {
      const entry = manager.entries.value.find((e) => e.itemId === item.id)
      if (entry !== undefined) manager.retry(entry.id)
      else await detail.retryItem(item.id)
      break
    }
  }
}

function openItem(id: string): void {
  itemPanelId.value = id
  itemPanelOpen.value = true
}

function navigateItem(id: string): void {
  itemPanelId.value = id
}

async function deleteFromPanel(id: string): Promise<void> {
  const index = items.value.findIndex((i) => i.id === id)
  const neighbour = items.value[index + 1] ?? items.value[index - 1] ?? null
  if (await detail.deleteItem(id)) {
    void storage.fetch()
    if (neighbour !== null) itemPanelId.value = neighbour.id
    else itemPanelOpen.value = false
  }
}

async function confirmDeleteItems(): Promise<void> {
  const ids = deleteIds.value
  if (ids === null) return
  deletingItems.value = true
  const ok = await detail.bulkDelete(ids)
  deletingItems.value = false
  if (ok) {
    deleteIds.value = null
    selection.clear()
    void storage.fetch()
  }
}

function cancelDeleteItems(): void {
  if (!deletingItems.value) deleteIds.value = null
}

async function setCoverFromPanel(id: string): Promise<void> {
  if (await detail.setCover(id)) toast.push(t('galleries.toast.cover_set'), { kind: 'success' })
}

// ── Bulk actions ─────────────────────────────────────────────────────────────

function selectedIds(): string[] {
  return [...selection.selected.value]
}

async function bulkHide(hidden: boolean): Promise<void> {
  const ids = selectedIds()
  await hideItems(ids, hidden)
  selection.clear()
}

async function moveTo(targetGalleryId: string): Promise<{ ok: boolean; code: string | null }> {
  return detail.bulkMove(moveIds.value, targetGalleryId)
}

function onMoved(): void {
  movePanelOpen.value = false
  toast.push(t('galleries.toast.moved', moveIds.value.length), { kind: 'success' })
  selection.clear()
  void galleryList.fetch()
  void detail.refresh(true)
}

function startCreateForMove(): void {
  movePanelOpen.value = false
  moveAfterCreate.value = true
  formPanelMode.value = 'create'
}

async function onGalleryCreated(created: OrganizerGallery): Promise<void> {
  formPanelMode.value = null
  if (!moveAfterCreate.value) return
  moveAfterCreate.value = false
  const result = await detail.bulkMove(moveIds.value, created.id)
  if (result.ok) {
    toast.push(t('galleries.toast.moved', moveIds.value.length), { kind: 'success' })
    selection.clear()
    void galleryList.fetch()
  } else {
    toast.push(t('galleries.errors.generic'), { kind: 'error' })
  }
}

function onGalleryFormClosed(): void {
  formPanelMode.value = null
  moveAfterCreate.value = false
}

function onGalleryUpdated(): void {
  formPanelMode.value = null
  void detail.refresh(true)
}

// ── Gallery-level actions ────────────────────────────────────────────────────

async function togglePublished(): Promise<void> {
  await detail.togglePublished()
}

async function confirmDeleteGallery(): Promise<void> {
  deletingGallery.value = true
  const ok = await galleryList.remove(galleryId)
  deletingGallery.value = false
  if (ok) {
    deleteGalleryOpen.value = false
    void router.push(`/organizer/events/${eventSlug}/galleries`)
  }
}

function cancelDeleteGallery(): void {
  if (!deletingGallery.value) deleteGalleryOpen.value = false
}

const deleteGalleryBody = computed(() =>
  gallery.value === null
    ? ''
    : isExternal.value
      ? t('galleries.delete_external_confirm', { title: gallery.value.title })
      : t('galleries.confirm.delete_gallery_body', {
        title: gallery.value.title,
        count: gallery.value.item_count,
        size: formatBytes(gallery.value.bytes_used),
      }),
)

const uploadingInThisGallery = computed(() =>
  ownEntries.value.some((e) => e.status !== UploadStatus.Ready && e.status !== UploadStatus.Failed && e.status !== UploadStatus.Cancelled),
)
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-28">
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">...</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/galleries`" class="hover:text-slate-300 transition-colors">
          {{ t('galleries.heading') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ gallery?.title ?? '...' }}</span>
      </nav>

      <!-- Loading -->
      <div v-if="loading && gallery === null" class="flex justify-center py-20" role="status" :aria-label="t('common.loading')">
        <AppSpinner />
      </div>

      <!-- Error -->
      <div
        v-else-if="error === 'generic'"
        class="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-8 text-center"
        role="alert"
      >
        <p class="text-sm text-red-300">{{ t('galleries.errors.load_failed') }}</p>
        <button
          type="button"
          class="mt-4 border border-red-400/50 text-red-200 hover:bg-red-500/10 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="detail.refresh()"
        >
          {{ t('galleries.errors.retry') }}
        </button>
      </div>

      <template v-else-if="gallery !== null">
        <!-- Header -->
        <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div class="min-w-0">
            <div class="flex items-center gap-3">
              <h1 class="text-3xl font-bold text-white truncate">{{ gallery.title }}</h1>
              <span
                class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                :class="isPublished ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700 text-slate-300'"
                data-testid="gallery-status-pill"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="isPublished ? 'bg-emerald-400' : 'bg-slate-400'" aria-hidden="true" />
                {{ isPublished ? t('galleries.card.published') : t('galleries.card.draft') }}
              </span>
              <span
                v-if="isExternal"
                class="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-500/15 px-2.5 py-1 text-xs font-medium text-sky-300"
                data-testid="gallery-external-badge"
              >
                {{ t('galleries.detail.external_badge') }}
              </span>
            </div>
            <p v-if="gallery.subtitle" class="text-slate-400 mt-1">{{ gallery.subtitle }}</p>
            <p v-if="!isExternal" class="text-xs text-slate-500 mt-2">{{ summaryLine }}</p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <a
              :href="previewUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
              :title="t('galleries.detail.preview_tooltip')"
              data-testid="preview-on-website"
            >
              {{ t('galleries.detail.preview') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
              @click="formPanelMode = 'edit'"
            >
              {{ t('galleries.detail.edit') }}
            </button>
            <button
              type="button"
              class="font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
              :class="isPublished
                ? 'border border-slate-600 hover:border-slate-500 text-slate-200'
                : 'bg-emerald-500 hover:bg-emerald-400 text-white'"
              data-testid="toggle-publish"
              @click="togglePublished"
            >
              {{ isPublished ? t('galleries.card.unpublish') : t('galleries.card.publish') }}
            </button>
            <button
              type="button"
              class="text-sm font-semibold text-red-400 hover:text-red-300 px-2 py-2"
              @click="deleteGalleryOpen = true"
            >
              {{ t('galleries.detail.delete') }}
            </button>
          </div>
        </div>

        <p v-if="gallery.description" class="mb-6 max-w-3xl whitespace-pre-line text-sm text-slate-400">{{ gallery.description }}</p>

        <!-- External gallery: no media management, only the link and the cover -->
        <section v-if="isExternal" class="mb-6 max-w-3xl space-y-4" data-testid="external-gallery-panel">
          <div class="rounded-2xl border border-slate-700 bg-slate-800 p-5">
            <h2 class="text-sm font-semibold text-slate-300 mb-2">{{ t('galleries.detail.open_link') }}</h2>
            <a
              v-if="gallery.external_url"
              :href="gallery.external_url"
              target="_blank"
              rel="noopener noreferrer"
              class="block truncate text-sm text-emerald-400 hover:text-emerald-300 underline"
              :title="gallery.external_url"
              data-testid="external-url-link"
            >
              {{ gallery.external_url }}
            </a>
          </div>
          <div class="rounded-2xl border border-slate-700 bg-slate-800 p-5 flex items-start gap-4">
            <div class="w-40 aspect-[4/3] shrink-0 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 flex items-center justify-center">
              <GalleryPicture v-if="gallery.cover" :image="gallery.cover" variant="medium" sizes="160px" />
            </div>
            <p v-if="coverProcessing" class="text-sm text-amber-300" data-testid="cover-processing">{{ t('galleries.cover.processing') }}</p>
            <p v-else-if="coverFailed" class="text-sm text-red-400" data-testid="cover-failed">{{ t('galleries.cover.failed') }}</p>
            <p v-else-if="gallery.cover === null" class="text-sm text-slate-400">{{ t('galleries.cover.none') }}</p>
          </div>
        </section>

        <template v-else>
        <div v-if="storage.summary.value" class="mb-6 space-y-4">
          <GalleryStorageMeter :summary="storage.summary.value" compact />
          <GalleryStorageBanner :summary="storage.summary.value" />
        </div>

        <!-- Preflight notices for this gallery -->
        <div v-if="notices.length > 0" class="mb-6 space-y-3">
          <GalleryPreflightBanner
            v-for="notice in notices"
            :key="notice.batchId"
            :notice="notice"
            @upload-fitting="manager.confirmPlan(notice.batchId)"
            @cancel="manager.cancelPlan(notice.batchId)"
            @dismiss="manager.dismissNotice(notice.batchId)"
          />
        </div>

        <!-- Empty and storage-full states -->
        <GalleryEmptyState
          v-if="!hasContent && uploadsBlocked"
          variant="storage-full"
          @add-link="linkPanelOpen = true"
        />
        <div v-else class="mb-6">
          <GalleryDropZone
            v-model:keep-original="keepOriginal"
            :disabled="uploadsBlocked"
            :disabled-reason="t('galleries.dropzone.disabled_reason')"
            :compact="hasContent"
            :image-max-bytes="limits.image_max_bytes"
            @files="handleFiles"
            @add-link="linkPanelOpen = true"
          />
        </div>

        <!-- Filter + grid -->
        <div v-if="hasContent" class="mb-4 flex flex-wrap items-center gap-2" role="group" :aria-label="t('galleries.detail.filter_label')">
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-sm font-medium transition-colors"
            :class="filter === 'all' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'"
            :aria-pressed="filter === 'all'"
            @click="filter = 'all'"
          >
            {{ t('galleries.detail.filter_all') }}
          </button>
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-sm font-medium transition-colors"
            :class="filter === 'missing_alt' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'"
            :aria-pressed="filter === 'missing_alt'"
            data-testid="filter-missing-alt"
            @click="filter = 'missing_alt'"
          >
            {{ t('galleries.detail.filter_missing_alt', { count: missingAltCount }) }}
          </button>
          <span v-if="uploadingInThisGallery" class="ml-auto text-xs text-slate-500">{{ t('galleries.detail.uploads_continue') }}</span>
        </div>

        <GalleryMediaGrid
          v-if="hasContent"
          :items="items"
          :pending="ownEntries"
          :selection="selection.selected.value"
          :filter="filter"
          :cover-item-id="gallery.cover_item_id"
          @select="(id, shift) => (shift ? selection.selectRange(id) : selection.toggle(id))"
          @open="openItem"
          @reorder="detail.reorderItems"
          @action="handleTileAction"
          @upload-action="onUploadAction"
        />
        </template>
      </template>
    </main>

    <!-- Page-wide drag overlay -->
    <div
      v-if="dragOverlay"
      class="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-emerald-500/10 backdrop-blur-sm border-4 border-dashed border-emerald-500"
      aria-hidden="true"
      data-testid="drag-overlay"
    >
      <p class="rounded-xl bg-slate-900/90 px-6 py-4 text-lg font-semibold text-white">{{ t('galleries.dropzone.overlay') }}</p>
    </div>

    <GallerySelectionBar
      v-if="!isExternal"
      :count="selection.count.value"
      :bytes="selection.selectedBytes.value"
      :all-selected="selection.count.value === visibleSelectable.length"
      @select-all="selection.selectAll()"
      @clear="selection.clear()"
      @hide="bulkHide(true)"
      @show="bulkHide(false)"
      @move="openMove(selectedIds())"
      @delete="deleteIds = selectedIds()"
    />

    <GalleryItemSlideOver
      :open="itemPanelOpen"
      :items="items"
      :item-id="itemPanelId"
      :cover-item-id="gallery?.cover_item_id ?? null"
      :save-item="detail.updateItem"
      @close="itemPanelOpen = false"
      @updated="() => void detail.refresh(true)"
      @navigate="navigateItem"
      @delete="deleteFromPanel"
      @set-cover="setCoverFromPanel"
      @retry="(id) => void detail.retryItem(id)"
    />

    <ExternalVideoSlideOver
      :open="linkPanelOpen"
      :default-credit="gallery?.default_credit ?? null"
      :add-video="detail.addExternalVideo"
      @close="linkPanelOpen = false"
      @created="linkPanelOpen = false"
    />

    <MoveItemsSlideOver
      :open="movePanelOpen"
      :source-gallery-id="galleryId"
      :item-count="moveIds.length"
      :galleries="galleryList.galleries.value"
      :move="moveTo"
      @close="movePanelOpen = false"
      @moved="onMoved"
      @create-new="startCreateForMove"
    />

    <GalleryFormSlideOver
      :open="formPanelMode !== null"
      :event-slug="eventSlug"
      :gallery="formPanelMode === 'edit' ? gallery : null"
      :items="formPanelMode === 'edit' ? items : undefined"
      @close="onGalleryFormClosed"
      @created="onGalleryCreated"
      @updated="onGalleryUpdated"
    />

    <ConfirmDialog
      :open="deleteIds !== null"
      :title="t('galleries.confirm.delete_items_title', deleteIds?.length ?? 0)"
      :body="deleteItemsBody"
      :confirm-label="t('galleries.item.delete')"
      :loading="deletingItems"
      @update:open="cancelDeleteItems"
      @confirm="confirmDeleteItems"
    />

    <ConfirmDialog
      :open="deleteGalleryOpen"
      :title="t('galleries.confirm.delete_gallery_title')"
      :body="deleteGalleryBody"
      :confirm-label="t('galleries.detail.delete')"
      :loading="deletingGallery"
      @update:open="cancelDeleteGallery"
      @confirm="confirmDeleteGallery"
    />
  </OrganizerLayout>
</template>
